// client/src/components/ArchivePage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';

const API = process.env.REACT_APP_API_URL || '';
const getToken = () => localStorage.getItem('ipe_token');
const authHeaders = () => ({ 'Content-Type': 'application/json', ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}) });
const OUTCOME_CONFIG = {
  dead:   { label: 'Dead',   colour: '#6b7280', bg: 'rgba(107,114,128,0.12)' },
  lost:   { label: 'Lost',   colour: '#ef4444', bg: 'rgba(239,68,68,0.10)'  },
  no_bid: { label: 'No Bid', colour: '#f59e0b', bg: 'rgba(245,158,11,0.10)' },
  won:    { label: 'Won',    colour: '#10b981', bg: 'rgba(16,185,129,0.10)' },
};
const PERIODS = [
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: '1y',  label: 'Last 12 months' },
  { value: 'all', label: 'All time' },
];

function fmtVal(p) {
  if (!p) return '\u2014';
  const v = p / 100;
  if (v >= 1000000) return `\u00a3${(v/1000000).toFixed(1)}m`;
  if (v >= 1000) return `\u00a3${Math.round(v/1000)}k`;
  return `\u00a3${v.toLocaleString()}`;
}
function fmtDate(ts) {
  if (!ts) return '\u2014';
  return new Date(ts).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' });
}

function DonutChart({ stats }) {
  const total = stats.reduce((s, r) => s + parseInt(r.count), 0);
  if (total === 0) return (
    <div style={{ textAlign:'center', color:'#64748b', padding:'32px 0', fontSize:13 }}>
      No archived records for this period
    </div>
  );
  const size = 180, cx = 90, cy = 90, r = 70, ir = 44;
  let cum = -Math.PI / 2;
  const slices = stats.map(row => {
    const pct = parseInt(row.count) / total;
    const a = pct * 2 * Math.PI;
    const s = cum; cum += a;
    return { ...row, pct, s, e: cum };
  });
  function slicePath(s, e) {
    const x1=cx+r*Math.cos(s), y1=cy+r*Math.sin(s);
    const x2=cx+r*Math.cos(e), y2=cy+r*Math.sin(e);
    const ix1=cx+ir*Math.cos(e), iy1=cy+ir*Math.sin(e);
    const ix2=cx+ir*Math.cos(s), iy2=cy+ir*Math.sin(s);
    const lg = e-s > Math.PI ? 1 : 0;
    return `M${x1} ${y1} A${r} ${r} 0 ${lg} 1 ${x2} ${y2} L${ix1} ${iy1} A${ir} ${ir} 0 ${lg} 0 ${ix2} ${iy2} Z`;
  }
  return (
    <div style={{ display:'flex', gap:24, alignItems:'center', flexWrap:'wrap' }}>
      <svg width={size} height={size} style={{ flexShrink:0 }}>
        {slices.map((sl, i) => {
          const cfg = OUTCOME_CONFIG[sl.outcome] || { colour:'#94a3b8' };
          if (sl.pct < 0.001) return null;
          if (sl.pct > 0.999) return <g key={i}><circle cx={cx} cy={cy} r={r} fill={cfg.colour} opacity={0.88} stroke="#0f172a" strokeWidth={1.5} /><circle cx={cx} cy={cy} r={ir} fill="#0f172a" /></g>;
          return <path key={i} d={slicePath(sl.s, sl.e)} fill={cfg.colour} opacity={0.88} stroke="#0f172a" strokeWidth={1.5} />;
        })}
        <text x={cx} y={cy-7} textAnchor="middle" fill="#f1f5f9" fontSize={22} fontWeight={700}>{total}</text>
        <text x={cx} y={cy+11} textAnchor="middle" fill="#64748b" fontSize={11}>total</text>
      </svg>
      <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
        {slices.map((sl, i) => {
          const cfg = OUTCOME_CONFIG[sl.outcome] || { label:sl.outcome, colour:'#94a3b8' };
          return (
            <div key={i} style={{ display:'flex', alignItems:'center', gap:8 }}>
              <div style={{ width:10, height:10, borderRadius:2, background:cfg.colour, flexShrink:0 }} />
              <span style={{ fontSize:12, color:'#cbd5e1', minWidth:52 }}>{cfg.label}</span>
              <span style={{ fontSize:12, fontWeight:600, color:'#f1f5f9', minWidth:22 }}>{sl.count}</span>
              <span style={{ fontSize:11, color:'#64748b' }}>({Math.round(sl.pct*100)}%)</span>
              {sl.total_value > 0 && <span style={{ fontSize:11, color:'#64748b' }}>{fmtVal(parseInt(sl.total_value))}</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// REQ-03: read-only "site address" modal opened from any archived row.
// No restore, no writes — just site_address + What3Words. Fetches from
// the appropriate endpoint by record_type ('opportunity' -> /api/tenders/:id,
// 'lead' -> /api/crm/leads/:id).
function AddressViewModal({ record, onClose }) {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const url = record.record_type === 'lead'
          ? `${API}/api/crm/leads/${record.id}`
          : `${API}/api/tenders/${record.id}`;
        const res = await fetch(url, { headers: authHeaders() });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) setDetail(data);
      } catch (e) { if (!cancelled) setError(e.message || 'Failed to load'); }
    })();
    return () => { cancelled = true; };
  }, [record]);

  const addr = detail?.site_address || null;
  const w3w  = detail?.what3words   || null;
  const ref  = record.reference || detail?.enq_reference || detail?.project_ref || '—';
  const name = record.name || detail?.project_name || detail?.title || '—';

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}
         onClick={onClose}>
      <div style={{ background:'#1e293b', border:'1px solid #334155', borderRadius:10, padding:24, width:460, maxWidth:'90vw' }}
           onClick={e => e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:12 }}>
          <div>
            <h3 style={{ margin:'0 0 4px', color:'#f1f5f9', fontSize:15 }}>{ref}</h3>
            <div style={{ color:'#94a3b8', fontSize:12 }}>{name}</div>
          </div>
          <span style={{ fontSize:10, color:'#64748b', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.06em', padding:'2px 7px', border:'1px solid #334155', borderRadius:4 }}>Archived</span>
        </div>
        <div style={{ height:1, background:'#334155', margin:'12px 0 16px' }} />
        {error && <div style={{ color:'#f87171', fontSize:12, marginBottom:12 }}>Could not load record: {error}</div>}
        {!detail && !error && <div style={{ color:'#64748b', fontSize:12, padding:'8px 0' }}>Loading…</div>}
        {detail && (
          <>
            <div style={{ marginBottom:14 }}>
              <div style={{ fontSize:10, color:'#64748b', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:4 }}>Site Address</div>
              <div style={{ color:'#e2e8f0', fontSize:13, whiteSpace:'pre-wrap', lineHeight:1.4, background:'#0f172a', padding:'8px 10px', borderRadius:5, border:'1px solid #334155', fontFamily:'inherit' }}>
                {addr || <span style={{ color:'#475569', fontStyle:'italic' }}>Not recorded</span>}
              </div>
            </div>
            <div style={{ marginBottom:14 }}>
              <div style={{ fontSize:10, color:'#64748b', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:4 }}>What3Words</div>
              <div style={{ color: w3w ? '#10b981' : '#475569', fontSize:13, fontFamily:"'DM Mono', monospace", background:'#0f172a', padding:'8px 10px', borderRadius:5, border:'1px solid #334155' }}>
                {w3w ? `///${w3w}` : <span style={{ fontStyle:'italic' }}>Not recorded</span>}
              </div>
            </div>
          </>
        )}
        <div style={{ display:'flex', justifyContent:'flex-end', marginTop:16 }}>
          <button onClick={onClose} style={{ padding:'7px 16px', borderRadius:6, border:'1px solid #334155', background:'transparent', color:'#94a3b8', cursor:'pointer', fontSize:13 }}>Close</button>
        </div>
        <div style={{ marginTop:12, fontSize:10, color:'#475569', textAlign:'center' }}>Read-only view. To restore or edit, use the Restore action or contact a Manager.</div>
      </div>
    </div>
  );
}

function RestoreModal({ record, onClose, onRestored }) {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  async function go() {
    if (reason.trim().length < 5) { setError('Min 5 characters required'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/archive/restore/${record.record_type}/${record.id}`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        headers: authHeaders(), body: JSON.stringify({ reason })
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed');
      onRestored(); onClose();
    } catch(e) { setError(e.message); } finally { setLoading(false); }
  }
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:'#1e293b', border:'1px solid #334155', borderRadius:10, padding:28, width:420, maxWidth:'90vw' }}>
        <h3 style={{ margin:'0 0 8px', color:'#f1f5f9', fontSize:16 }}>Restore Record</h3>
        <p style={{ margin:'0 0 16px', color:'#94a3b8', fontSize:13 }}>
          Restoring <strong style={{ color:'#e2e8f0' }}>{record.name}</strong> to the active pipeline. A reason is required.
        </p>
        <textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason for restoration\u2026" rows={3}
          style={{ width:'100%', boxSizing:'border-box', background:'#0f172a', border:'1px solid #334155', borderRadius:6, color:'#f1f5f9', fontSize:13, padding:'8px 10px', resize:'vertical', outline:'none', fontFamily:'inherit' }} />
        {error && <div style={{ color:'#f87171', fontSize:12, marginTop:6 }}>{error}</div>}
        <div style={{ display:'flex', gap:8, marginTop:16, justifyContent:'flex-end' }}>
          <button onClick={onClose} style={{ padding:'7px 16px', borderRadius:6, border:'1px solid #334155', background:'transparent', color:'#94a3b8', cursor:'pointer', fontSize:13 }}>Cancel</button>
          <button onClick={go} disabled={loading} style={{ padding:'7px 16px', borderRadius:6, border:'none', background:'#0e9aaa', color:'#fff', cursor:'pointer', fontSize:13, opacity:loading?0.6:1 }}>
            {loading ? 'Restoring\u2026' : 'Restore'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ArchivePage({ currentUser }) {
  const [records, setRecords] = useState([]);
  const [stats, setStats] = useState([]);
  const [pagination, setPagination] = useState({});
  const [outcome, setOutcome] = useState('all');
  const [period, setPeriod] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [viewTarget, setViewTarget] = useState(null); // REQ-03: address view modal
  const canRestore = ['manager','director'].includes(currentUser?.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ outcome, period, page, limit:50, search });
      const res = await fetch(`${API}/api/archive?${p}`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setRecords(data.records); setStats(data.stats); setPagination(data.pagination);
    } catch(e) { console.error(e); } finally { setLoading(false); }
  }, [outcome, period, page, search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [outcome, period, search]);

  const btn = { padding:'5px 14px', borderRadius:5, border:'1px solid #334155', background:'transparent', color:'#94a3b8', cursor:'pointer', fontSize:12 };
  const btnA = { ...btn, border:'1px solid #0e9aaa', background:'rgba(14,154,170,0.12)', color:'#0e9aaa' };

  return (
    <div style={{ padding:'24px 28px', maxWidth:1200, margin:'0 auto' }}>
      <div style={{ marginBottom:24 }}>
        <h2 style={{ margin:'0 0 4px', fontSize:18, fontWeight:600, color:'#f1f5f9' }}>Archive</h2>
        <p style={{ margin:0, fontSize:13, color:'#64748b' }}>Historical record of all closed pipeline activity</p>
      </div>

      <div style={{ background:'#1e293b', border:'1px solid #1e3a5f', borderRadius:10, padding:20, marginBottom:20, display:'flex', gap:32, flexWrap:'wrap', alignItems:'flex-start' }}>
        <div style={{ flex:'0 0 auto' }}>
          <div style={{ fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.06em', color:'#64748b', marginBottom:8 }}>Outcome Breakdown</div>
          <DonutChart stats={stats} />
        </div>
        <div style={{ flex:1, minWidth:220 }}>
          <div style={{ fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.06em', color:'#64748b', marginBottom:6 }}>Period</div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:18 }}>
            {PERIODS.map(o => <button key={o.value} onClick={() => setPeriod(o.value)} style={period===o.value?btnA:btn}>{o.label}</button>)}
          </div>
          <div style={{ fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.06em', color:'#64748b', marginBottom:6 }}>Filter</div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
            <button onClick={() => setOutcome('all')} style={outcome==='all'?btnA:btn}>All</button>
            {Object.entries(OUTCOME_CONFIG).map(([k,cfg]) => (
              <button key={k} onClick={() => setOutcome(k)}
                style={outcome===k ? { ...btn, border:`1px solid ${cfg.colour}`, background:cfg.bg, color:cfg.colour } : btn}>
                {cfg.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div style={{ background:'#1e293b', border:'1px solid #1e3a5f', borderRadius:10, padding:20 }}>
        <div style={{ display:'flex', gap:10, marginBottom:16 }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name\u2026"
            style={{ flex:1, padding:'7px 12px', borderRadius:6, border:'1px solid #334155', background:'#0f172a', color:'#f1f5f9', fontSize:13, outline:'none', fontFamily:'inherit' }} />
          {loading && <span style={{ color:'#64748b', fontSize:12, alignSelf:'center' }}>Loading\u2026</span>}
        </div>

        {records.length === 0 && !loading ? (
          <div style={{ textAlign:'center', color:'#64748b', padding:'40px 0', fontSize:13 }}>No archived records match the current filters</div>
        ) : (
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead>
                <tr style={{ borderBottom:'1px solid #1e3a5f' }}>
                  {['Reference','Name','Company','Outcome','Value','Archived','Reason', canRestore?'':''].filter(h=>h!==undefined).map((h,i) => (
                    <th key={i} style={{ padding:'8px 10px', textAlign:'left', color:'#64748b', fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.05em', whiteSpace:'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {records.map(r => {
                  const cfg = OUTCOME_CONFIG[r.outcome] || { label:r.outcome, colour:'#64748b', bg:'rgba(100,116,139,0.1)' };
                  const narrative = r.reason_narrative || r.loss_narrative || '\u2014';
                  return (
                    <tr key={r.record_type+r.id}
                        onClick={() => setViewTarget(r)}
                        style={{ borderBottom:'1px solid #0f172a', cursor:'pointer' }}
                        title="Open address view (read-only)"
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(14,154,170,0.05)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ padding:'9px 10px', color:'#0e9aaa', fontFamily:'monospace', fontSize:12, whiteSpace:'nowrap' }}>{r.reference||'\u2014'}</td>
                      <td style={{ padding:'9px 10px', color:'#e2e8f0', maxWidth:200 }}>
                        <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.name}</div>
                      </td>
                      <td style={{ padding:'9px 10px', color:'#94a3b8', whiteSpace:'nowrap' }}>{r.company_display||'\u2014'}</td>
                      <td style={{ padding:'9px 10px' }}>
                        <span style={{ display:'inline-block', padding:'2px 8px', borderRadius:4, fontSize:11, fontWeight:600, background:cfg.bg, color:cfg.colour, border:`1px solid ${cfg.colour}40` }}>{cfg.label}</span>
                      </td>
                      <td style={{ padding:'9px 10px', color:'#94a3b8', fontFamily:'monospace', fontSize:12, whiteSpace:'nowrap' }}>{fmtVal(r.value_pence)}</td>
                      <td style={{ padding:'9px 10px', color:'#64748b', fontSize:12, whiteSpace:'nowrap' }}>
                        <div>{fmtDate(r.archived_at)}</div>
                        <div style={{ fontSize:11, color:'#475569' }}>{r.archived_by_name || 'Auto'}</div>
                      </td>
                      <td style={{ padding:'9px 10px', color:'#94a3b8', maxWidth:220 }}>
                        <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={narrative}>{narrative}</div>
                      </td>
                      {canRestore && (
                        <td style={{ padding:'9px 10px' }} onClick={e => e.stopPropagation()}>
                          {!r.restored_at
                            ? <button onClick={() => setRestoreTarget(r)}
                                style={{ padding:'3px 10px', borderRadius:4, border:'1px solid #334155', background:'transparent', color:'#94a3b8', cursor:'pointer', fontSize:11 }}
                                onMouseEnter={e=>{e.target.style.borderColor='#0e9aaa';e.target.style.color='#0e9aaa';}}
                                onMouseLeave={e=>{e.target.style.borderColor='#334155';e.target.style.color='#94a3b8';}}>Restore</button>
                            : <span style={{ fontSize:11, color:'#64748b' }} title={r.restore_reason}>Restored</span>
                          }
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {pagination.pages > 1 && (
          <div style={{ display:'flex', gap:6, justifyContent:'center', marginTop:16 }}>
            <button disabled={page===1} onClick={() => setPage(p=>p-1)} style={btn}>\u2190 Prev</button>
            <span style={{ color:'#64748b', fontSize:12, alignSelf:'center' }}>Page {page} of {pagination.pages}</span>
            <button disabled={page===pagination.pages} onClick={() => setPage(p=>p+1)} style={btn}>Next \u2192</button>
          </div>
        )}
      </div>

      {restoreTarget && <RestoreModal record={restoreTarget} onClose={() => setRestoreTarget(null)} onRestored={load} />}
      {viewTarget && <AddressViewModal record={viewTarget} onClose={() => setViewTarget(null)} />}
    </div>
  );
}
